import React, { useCallback, useEffect, useState } from 'react';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import { formatDate, formatRelativeTime } from '@/utils';
import type { LetterRecord } from '@/types/letters';
import {
  RiArrowLeftLine as BackIcon,
  RiFileWordLine,
  RiGoogleLine,
  RiDownloadLine,
} from 'react-icons/ri';
import LetterEditor from '@/components/letters/editor/LetterEditor';
import SourcesSidebar from '@/components/letters/editor/SourcesSidebar';
import LetterGenerationProgress from '@/components/letters/LetterGenerationProgress';
import { LettersApi } from '@/api';
import { analytics } from '@/utils/analytics';
import { ANALYTICS_EVENTS } from '@/utils/analytics-events';

function formatLetterType(t?: string) {
  if (!t) return 'Letter';
  const map: Record<string, string> = {
    recommendation: 'Recommendation',
    cover_letter: 'Cover letter',
    petition: 'Petition',
    other: 'Other',
  };
  return map[t] || t.replace(/_/g, ' ').replace(/\b\w/g, s => s.toUpperCase());
}

interface LetterViewerProps {
  letter: LetterRecord;
  onBack: () => void;
  caseId?: string;
}

const LetterViewer: React.FC<LetterViewerProps> = ({
  letter: initialLetter,
  onBack,
  caseId,
}) => {
  const [isLoadingSources, setIsLoadingSources] = useState<boolean>(false);
  const [sources, setSources] = useState<
    Record<string, { text: string; score: number; queryText: string }[]>
  >({});
  const [taskId, setTaskId] = useState<string | null>(null);
  const [letter, setLetter] = useState<LetterRecord>(initialLetter);

  // Debug logging for letter ID changes
  useEffect(() => {
    console.log('LetterViewer - letter state updated:', {
      id: letter.id,
      status: letter.status,
      hasContent: !!letter.content,
    });
  }, [letter.id, letter.status]);

  // Check if letter is still being generated
  const isGenerating =
    letter.status === 'generating' || letter.status === 'processing';

  // Try to find the task ID from letter metadata
  useEffect(() => {
    if (isGenerating && letter.metadata) {
      const metadata = letter.metadata as any;
      if (metadata.taskId) {
        setTaskId(metadata.taskId);
      }
    }
  }, [isGenerating, letter.metadata]);

  // Poll for letter updates when it's being generated
  useEffect(() => {
    if (!isGenerating || !caseId || !letter.id) return;

    const pollInterval = setInterval(async () => {
      try {
        const letters = await LettersApi.listLetters(caseId);
        const updatedLetter = letters.find(
          (l: LetterRecord) => l.id === letter.id
        );

        if (updatedLetter && updatedLetter.id) {
          // Only update if the new letter has a valid id
          setLetter(updatedLetter);

          // If letter is now ready, stop polling
          if (
            updatedLetter.status !== 'generating' &&
            updatedLetter.status !== 'processing'
          ) {
            clearInterval(pollInterval);
          }
        }
      } catch (error) {
        console.error('Failed to poll letter status:', error);
      }
    }, 8000); // Poll every 8 seconds

    return () => clearInterval(pollInterval);
  }, [isGenerating, caseId, letter.id]);

  const handleExportToWord = useCallback(async () => {
    try {
      analytics.track(ANALYTICS_EVENTS.LETTER_DOWNLOAD_WORD_CLICKED, {
        case_id: caseId,
        letter_id: letter.id,
        letter_type: letter.letter_type,
      });
      const apiUrl = (import.meta as any).env?.VITE_API_URL || 'http://localhost:3001';
      const url = `${apiUrl}/api/letters/${letter.id}/export/docx`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to export letter');
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `letter-${letter.id?.substring(0, 8) || 'download'}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Export to Word failed:', error);
      alert('Failed to export letter to Word');
    }
  }, [caseId, letter.id, letter.letter_type]);

  const handleExportToGoogleDocs = useCallback(async () => {
    try {
      analytics.track(ANALYTICS_EVENTS.LETTER_OPEN_GOOGLE_DOCS_CLICKED, {
        case_id: caseId,
        letter_id: letter.id,
        letter_type: letter.letter_type,
      });
      const apiUrl = (import.meta as any).env?.VITE_API_URL || 'http://localhost:3001';
      const url = `${apiUrl}/api/letters/${letter.id}/export/google-docs`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to export letter');
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `letter-${letter.id?.substring(0, 8) || 'download'}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Export to Google Docs failed:', error);
      alert('Failed to export letter for Google Docs');
    }
  }, [caseId, letter.id, letter.letter_type]);

  const handleCheckSources = useCallback(async (q: string) => {
    const caseIdForSearch: string | undefined =
      caseId ||
      (letter as any).project_id ||
      (letter as any).case_id ||
      (letter as any).caseId;

    console.log('handleCheckSources called', {
      query: q,
      caseId,
      caseIdForSearch,
      letterKeys: Object.keys(letter),
    });

    if (!caseIdForSearch) {
      console.error('Missing caseId for sources search. Letter data:', {
        caseId,
        project_id: (letter as any).project_id,
        case_id: (letter as any).case_id,
        letterCaseId: (letter as any).caseId,
      });
      alert('Cannot check sources: This letter is not associated with a case.');
      return;
    }
    const query = (q || '').trim();
    if (!query || query.length < 3) {
      console.warn('Query too short:', query);
      return;
    }

    try {
      setSources({});
      setIsLoadingSources(true);
      console.log('Searching documents with:', { caseIdForSearch, query });

      const res = await LettersApi.searchInDocuments({
        case_id: caseIdForSearch,
        query,
        search_type: 'fuzzy',
        max_results: 8,
      });

      console.log('Search results:', res);

      const grouped: Record<
        string,
        { text: string; score: number; queryText: string }[]
      > = {};
      (res?.results || []).forEach(r => {
        const file = r.fileName || 'Document';
        if (!grouped[file]) grouped[file] = [];
        grouped[file].push({
          text: r.content,
          score: r.relevance,
          queryText: res.query || query,
        });
      });
      setSources(grouped);
    } catch (e) {
      console.error('Sources search failed', e);
      alert('Failed to search sources. Check the console for details.');
    } finally {
      setIsLoadingSources(false);
    }
  }, [caseId, letter]);

  // If letter is still being generated, show the progress interface
  if (isGenerating && taskId) {
    return (
      <LetterGenerationProgress
        taskId={taskId}
        onBack={onBack}
        onFinal={() => {
          // Refresh the letter list when generation completes
          onBack();
        }}
      />
    );
  }

  // If letter is generating but we don't have a taskId, show a message
  if (isGenerating) {
    return (
      <div className="pb-4 w-full bg-neutral-50 min-h-screen">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="inline-flex items-center gap-1"
          >
            <BackIcon className="h-4 w-4" /> Back
          </Button>
          <h3 className="text-lg font-semibold text-neutral-900">
            {letter.letterType ||
              letter.title ||
              formatLetterType(letter.letter_type)}
          </h3>
        </div>

        <div className="max-w-6xl w-full mx-auto relative mt-4">
          <Card padding="lg" className="text-center space-y-4">
            <div className="flex items-center justify-center pt-12 pb-6">
              <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-blue-600 animate-spin"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              </div>
            </div>
            <h4 className="text-lg font-semibold text-gray-900">
              Letter is being generated
            </h4>
            <p className="text-sm text-gray-600">Please wait a moment...</p>
            <Button onClick={onBack} variant="outline">
              Back to Letters
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-4 w-full bg-neutral-50 min-h-screen">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="inline-flex items-center gap-1"
          >
            <BackIcon className="h-4 w-4" /> Back
          </Button>
          <h3 className="text-lg font-semibold text-neutral-900">
            {letter.letterType ||
              letter.title ||
              formatLetterType(letter.letter_type)}
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportToWord}
            className="inline-flex items-center gap-1"
          >
            <RiFileWordLine className="h-4 w-4" /> Edit in Word
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportToGoogleDocs}
            className="inline-flex items-center gap-1"
          >
            <RiGoogleLine className="h-4 w-4" /> Edit in Google Docs
          </Button>
        </div>
      </div>

      <div className="max-w-6xl w-full mx-auto relative mt-4">
        <div className="flex items-center gap-4 text-xs text-neutral-600 px-2">
          {letter.created_at && (
            <span>Created {formatDate(letter.created_at)}</span>
          )}
          {letter.updated_at && (
            <span>Updated {formatRelativeTime(letter.updated_at)}</span>
          )}
        </div>

        <div className="w-full mt-4 flex">
          <div className="flex-1 min-w-0">
            <LetterEditor
              initialContent={letter.content || ''}
              onCheckSources={handleCheckSources}
              letterId={letter.id}
              caseId={
                caseId ||
                (letter as any).project_id ||
                (letter as any).case_id ||
                (letter as any).caseId
              }
            />
          </div>
          <div className="hidden lg:block ml-4 w-80">
            <SourcesSidebar
              sources={sources}
              isLoading={isLoadingSources}
              caseId={
                caseId ||
                (letter as any).project_id ||
                (letter as any).case_id ||
                (letter as any).caseId
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LetterViewer;
